import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { splitSheetsAPI } from '../services/api';
import HelpGuide from '../components/HelpGuide';

interface Collaborator {
  name: string;
  email: string;
  role: string;
  percentage: number;
}

const CreateSplitSheetPage: React.FC = () => {
  const [songTitle, setSongTitle] = useState('');
  const [songDetails, setSongDetails] = useState('');
  const [collaborators, setCollaborators] = useState<Collaborator[]>([
    { name: '', email: '', role: 'Artist', percentage: 0 }
  ]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  const navigate = useNavigate();

  const addCollaborator = () => {
    setCollaborators([...collaborators, { name: '', email: '', role: 'Artist', percentage: 0 }]);
  };

  const removeCollaborator = (index: number) => {
    if (collaborators.length > 1) {
      const newCollaborators = collaborators.filter((_, i) => i !== index);
      setCollaborators(newCollaborators);
    }
  };

  const updateCollaborator = (index: number, field: keyof Collaborator, value: string | number) => {
    const newCollaborators = [...collaborators];
    newCollaborators[index] = { ...newCollaborators[index], [field]: value };
    setCollaborators(newCollaborators);
  };

  const calculateTotalPercentage = () => {
    return collaborators.reduce((sum, collab) => sum + (parseFloat(String(collab.percentage)) || 0), 0);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!songTitle.trim()) {
      setError('Song title is required');
      return;
    }

    if (collaborators.some(c => !c.name.trim() || !c.email.trim())) {
      setError('All collaborators must have a name and email');
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (collaborators.some(c => !emailRegex.test(c.email))) {
      setError('Please enter valid email addresses for all collaborators');
      return;
    }

    const total = calculateTotalPercentage();
    if (Math.abs(total - 100) > 0.01) {
      setError(`Total percentage must equal 100%. Current total: ${total.toFixed(2)}%`);
      return;
    }

    // Validate percentages are positive
    if (collaborators.some(c => c.percentage < 0)) {
      setError('All percentages must be positive numbers');
      return;
    }

    setLoading(true);

    try {
      const requestData = {
        songTitle,
        songDetails: songDetails || '',
        collaborators: collaborators.map(c => ({
          name: c.name.trim(),
          email: c.email.trim(),
          role: c.role,
          percentage: parseFloat(String(c.percentage))
        }))
      };

      const response = await splitSheetsAPI.create(requestData);
      navigate(`/split-sheets/${response.data.splitSheet.id}`);
    } catch (err: any) {
      
      const errorMessage = err.response?.data?.error || 
                          err.response?.data?.message || 
                          err.message || 
                          'Failed to create split sheet';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const totalPercentage = calculateTotalPercentage();
  const isValid = Math.abs(totalPercentage - 100) < 0.01;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
      <div className="mb-6 sm:mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Create Split Sheet</h1>
        <p className="mt-1 text-sm text-gray-600">
          Define ownership and royalty splits for your musical work
        </p>
      </div>
      <div className="mb-6">
        <HelpGuide
          title="Before you submit"
          intro="Follow this order to avoid validation errors."
          steps={[
            { title: 'Song info first', detail: 'Add title and optional details.' },
            { title: 'Add all collaborators', detail: 'Each person needs name, email, role, and percentage.' },
            { title: 'Make total 100%', detail: 'Adjust ownership until total equals exactly 100%.' },
          ]}
        />
      </div>

      <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
        {error && (
          <div className="bg-red-50 border border-red-400 text-red-700 px-4 py-3 rounded text-sm">
            {error}
          </div>
        )}

        {/* Song Information */}
        <div className="bg-white shadow rounded-lg p-4 sm:p-6">
          <h2 className="text-base sm:text-lg font-medium text-gray-900 mb-4">Song Information</h2>
          
          <div className="space-y-4">
            <div>
              <label htmlFor="songTitle" className="block text-sm font-medium text-gray-700">
                Song Title *
              </label>
              <input
                type="text"
                id="songTitle"
                required
                value={songTitle}
                onChange={(e) => setSongTitle(e.target.value)}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                placeholder="Enter song title"
              />
            </div>

            <div>
              <label htmlFor="songDetails" className="block text-sm font-medium text-gray-700">
                Additional Details (Optional)
              </label>
              <textarea
                id="songDetails"
                rows={3}
                value={songDetails}
                onChange={(e) => setSongDetails(e.target.value)}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                placeholder="Album, genre, release date, etc."
              />
            </div>
          </div>
        </div>

        {/* Collaborators */}
        <div className="bg-white shadow rounded-lg p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 sm:gap-0 mb-4">
            <h2 className="text-base sm:text-lg font-medium text-gray-900">Collaborators</h2>
            <button
              type="button"
              onClick={addCollaborator}
              className="inline-flex items-center justify-center px-3 py-2 sm:py-1 border border-transparent text-sm font-medium rounded-md text-primary-700 bg-primary-100 hover:bg-primary-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              <svg className="-ml-0.5 mr-1 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add Collaborator
            </button>
          </div>

          <div className="space-y-4">
            {collaborators.map((collaborator, index) => (
              <div key={index} className="border border-gray-200 rounded-md p-4">
                <div className="flex justify-between items-start mb-3">
                  <h3 className="text-sm font-medium text-gray-700">
                    Collaborator {index + 1}
                  </h3>
                  {collaborators.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeCollaborator(index)}
                      className="text-red-600 hover:text-red-800 text-sm font-medium"
                    >
                      Remove
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Name *</label>
                    <input
                      type="text"
                      required
                      value={collaborator.name}
                      onChange={(e) => updateCollaborator(index, 'name', e.target.value)}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                      placeholder="Collaborator name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Email *</label>
                    <input
                      type="email"
                      required
                      value={collaborator.email}
                      onChange={(e) => updateCollaborator(index, 'email', e.target.value)}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                      placeholder="email@example.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Role</label>
                    <select
                      value={collaborator.role}
                      onChange={(e) => updateCollaborator(index, 'role', e.target.value)}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    >
                      <option>Artist</option>
                      <option>Producer</option>
                      <option>Songwriter</option>
                      <option>Composer</option>
                      <option>Featured Artist</option>
                      <option>Other</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Percentage (%) *</label>
                    <input
                      type="number"
                      required
                      min="0"
                      max="100"
                      step="0.01"
                      value={collaborator.percentage}
                      onChange={(e) => updateCollaborator(index, 'percentage', parseFloat(e.target.value) || 0)}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                      placeholder="0.00"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Total Percentage Display */}
          <div className={`mt-4 p-3 rounded-md ${isValid ? 'bg-green-50 border border-green-200' : 'bg-yellow-50 border border-yellow-200'}`}>
            <div className="flex justify-between items-center">
              <span className={`text-sm font-medium ${isValid ? 'text-green-800' : 'text-yellow-800'}`}>
                Total Percentage:
              </span>
              <span className={`text-lg font-bold ${isValid ? 'text-green-900' : 'text-yellow-900'}`}>
                {totalPercentage.toFixed(2)}%
              </span>
            </div>
            {!isValid && (
              <p className="mt-1 text-xs text-yellow-700">
                Must equal 100%. Adjust percentages to continue.
              </p>
            )}
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 sm:gap-4">
          <button
            type="button"
            onClick={() => navigate('/dashboard')}
            className="w-full sm:w-auto px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading || !isValid}
            className="w-full sm:w-auto px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Creating...' : 'Create Split Sheet'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateSplitSheetPage;

